import React, { useState, useEffect } from 'react';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import {
  Trophy,
  TrendingUp,
  Crown,
  Star,
  Medal,
  ChevronUp,
  ChevronDown,
  Info
} from 'lucide-react';

// Aspirational benchmark data - seeded elite profiles
const SEEDED_RANKINGS = [
  { id: 'elite-1', name: 'Marcus J.', score: 98, school: 'IMG Academy', sport: 'Basketball', isSeeded: true, badge: 'Elite Prospect' },
  { id: 'elite-2', name: 'Jaylen T.', score: 97, school: 'Oak Hill', sport: 'Basketball', isSeeded: true, badge: 'Elite Prospect' },
  { id: 'elite-3', name: 'DeShawn M.', score: 95, school: 'Montverde', sport: 'Football', isSeeded: true, badge: 'Elite Prospect' },
  { id: 'seed-4', name: 'Amir K.', score: 92, school: 'Sunrise Christian', sport: 'Basketball', isSeeded: true },
  { id: 'seed-5', name: 'Chris P.', score: 90, school: 'La Lumiere', sport: 'Basketball', isSeeded: true },
  { id: 'seed-6', name: 'Terrance W.', score: 88, school: 'St. Thomas More', sport: 'Football', isSeeded: true },
  { id: 'seed-7', name: 'Malik R.', score: 86, school: 'Link Academy', sport: 'Basketball', isSeeded: true },
  { id: 'seed-8', name: 'Jordan B.', score: 84, school: 'Wasatch Academy', sport: 'Basketball', isSeeded: true },
  { id: 'seed-9', name: 'Isaiah H.', score: 82, school: 'AZ Compass Prep', sport: 'Football', isSeeded: true },
  { id: 'seed-10', name: 'Darius L.', score: 80, school: 'Prolific Prep', sport: 'Basketball', isSeeded: true },
  { id: 'seed-11', name: 'Kevon S.', score: 78, school: 'Hillcrest Prep', sport: 'Basketball', isSeeded: true },
  { id: 'seed-12', name: 'Tyrese G.', score: 75, school: 'Dream City', sport: 'Football', isSeeded: true },
  { id: 'seed-13', name: 'Andre C.', score: 72, school: 'Vertical Academy', sport: 'Basketball', isSeeded: true },
  { id: 'seed-14', name: 'Brandon M.', score: 68, school: 'Word of God', sport: 'Basketball', isSeeded: true },
  { id: 'seed-15', name: 'Cameron D.', score: 65, school: 'Wheeler HS', sport: 'Football', isSeeded: true }
];

const RankBadge = ({ rank }) => {
  if (rank === 1) return <Crown className="w-5 h-5 text-yellow-400" />;
  if (rank === 2) return <Medal className="w-5 h-5 text-gray-300" />;
  if (rank === 3) return <Medal className="w-5 h-5 text-amber-600" />;
  return <span className="text-gray-500 font-mono text-sm">#{rank}</span>;
};

const NationalScoutRanking = ({ userScore = 0, userName = 'You', userSchool = 'PRSVR Academy' }) => {
  const [rankings, setRankings] = useState([]);
  const [userRank, setUserRank] = useState(0);
  const [isInTopTen, setIsInTopTen] = useState(false);
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 10 });

  useEffect(() => {
    // Merge user with seeded data
    const userEntry = {
      id: 'current-user',
      name: userName,
      score: userScore,
      school: userSchool,
      sport: 'Academy',
      isUser: true
    };

    // Combine and sort by score
    const allRankings = [...SEEDED_RANKINGS, userEntry]
      .sort((a, b) => b.score - a.score)
      .map((entry, index) => ({ ...entry, rank: index + 1 }));

    const userIndex = allRankings.findIndex(r => r.isUser);
    const userRankNum = userIndex + 1;
    const totalRankings = allRankings.length;
    const topTenThreshold = Math.ceil(totalRankings * 0.1);

    setRankings(allRankings);
    setUserRank(userRankNum);
    setIsInTopTen(userRankNum <= topTenThreshold);

    // Determine visible range based on user position
    if (userRankNum <= 5) {
      // User is near top, show top 10
      setVisibleRange({ start: 0, end: 10 });
    } else if (userRankNum > totalRankings * 0.5) {
      // User is in bottom 50%, show 3 above, user, 3 below
      const start = Math.max(0, userIndex - 3);
      const end = Math.min(totalRankings, userIndex + 4);
      setVisibleRange({ start, end });
    } else {
      // User is in middle, show surrounding context
      const start = Math.max(0, userIndex - 3);
      const end = Math.min(totalRankings, userIndex + 4);
      setVisibleRange({ start, end });
    }
  }, [userScore, userName, userSchool]);

  const visibleRankings = rankings.slice(visibleRange.start, visibleRange.end);
  const showTopThree = visibleRange.start > 0;

  return (
    <Card className="bg-gradient-to-br from-[#0a1628] to-[#0d1e36] border-2 border-[#1e3a5f] overflow-hidden">
      {/* Header - ESPN/Draft Board Style */}
      <div className="bg-gradient-to-r from-[#0f2744] to-[#162d50] p-4 border-b border-[#1e3a5f]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center border border-orange-500/50">
              <Trophy className="w-5 h-5 text-orange-500" />
            </div>
            <div>
              <h3 className="text-white font-black text-lg uppercase tracking-wider">National Scout Ranking</h3>
              <p className="text-[#4a6fa5] text-xs uppercase tracking-widest">Power Index</p>
            </div>
          </div>
          <Badge className={`${isInTopTen ? 'bg-orange-500 text-black' : 'bg-[#1e3a5f] text-[#4a6fa5]'} font-bold`}>
            {isInTopTen ? 'TOP 10%' : `RANK #${userRank}`}
          </Badge>
        </div>
      </div>

      <CardContent className="p-0">
        {/* Top 3 Locked Display (if user not in top range) */}
        {showTopThree && (
          <div className="border-b border-[#1e3a5f]/50 bg-[#0d1e36]/50">
            <div className="px-4 py-2 text-[#4a6fa5] text-xs uppercase tracking-wider flex items-center gap-2">
              <Crown className="w-3 h-3" /> Elite Tier
            </div>
            {rankings.slice(0, 3).map((entry) => (
              <div
                key={entry.id}
                className="flex items-center justify-between px-4 py-3 border-b border-[#1e3a5f]/30 bg-gradient-to-r from-yellow-500/5 to-transparent"
              >
                <div className="flex items-center gap-3">
                  <RankBadge rank={entry.rank} />
                  <div>
                    <p className="text-white font-bold text-sm">{entry.name}</p>
                    <p className="text-[#4a6fa5] text-xs">{entry.school}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {entry.badge && (
                    <Badge className="bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 text-xs">
                      {entry.badge}
                    </Badge>
                  )}
                  <span className="text-orange-500 font-black text-lg">{entry.score}</span>
                </div>
              </div>
            ))}
            <div className="px-4 py-2 text-center">
              <span className="text-[#4a6fa5] text-xs">• • •</span>
            </div>
          </div>
        )}

        {/* Main Rankings List */}
        <div className="divide-y divide-[#1e3a5f]/30">
          {visibleRankings.map((entry, idx) => {
            const isCurrentUser = entry.isUser;
            const prevRank = idx > 0 ? visibleRankings[idx - 1]?.rank : null;
            const showGap = prevRank && entry.rank - prevRank > 1;

            return (
              <React.Fragment key={entry.id}>
                {showGap && (
                  <div className="px-4 py-1 text-center bg-[#0d1e36]/30">
                    <span className="text-[#4a6fa5] text-xs">• • •</span>
                  </div>
                )}
                <div
                  className={`flex items-center justify-between px-4 py-3 transition-all ${
                    isCurrentUser
                      ? 'bg-gradient-to-r from-orange-500/20 to-orange-500/5 border-l-4 border-l-orange-500'
                      : entry.rank <= 3
                      ? 'bg-gradient-to-r from-yellow-500/10 to-transparent'
                      : 'hover:bg-[#1e3a5f]/20'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 flex justify-center">
                      <RankBadge rank={entry.rank} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className={`font-bold text-sm ${isCurrentUser ? 'text-orange-500' : 'text-white'}`}>
                          {entry.name}
                          {isCurrentUser && <span className="text-orange-400 ml-1">(You)</span>}
                        </p>
                        {entry.rank <= 3 && !isCurrentUser && (
                          <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                        )}
                      </div>
                      <p className="text-[#4a6fa5] text-xs">{entry.school}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {entry.badge && (
                      <Badge className="bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 text-xs hidden sm:flex">
                        {entry.badge}
                      </Badge>
                    )}
                    <div className={`font-black text-lg ${isCurrentUser ? 'text-orange-500' : 'text-white'}`}>
                      {entry.score}
                    </div>
                  </div>
                </div>
              </React.Fragment>
            );
          })}
        </div>

        {/* Scout's Note */}
        <div className="p-4 bg-gradient-to-r from-[#0f2744] to-[#162d50] border-t border-[#1e3a5f]">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center shrink-0">
              <Info className="w-4 h-4 text-blue-400" />
            </div>
            <div>
              <p className="text-blue-400 font-bold text-xs uppercase tracking-wider mb-1">Scout's Note</p>
              <p className="text-[#8fabc7] text-sm leading-relaxed">
                College scouts prioritize IQ and Logic. Move into the <span className="text-orange-500 font-bold">Top 10%</span> to 
                unlock the <span className="text-yellow-400 font-bold">'Elite Prospect'</span> badge on your profile.
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default NationalScoutRanking;
